import React, { useState } from 'react'
import Modal from '@mui/material/Modal'
import Backdrop from '@mui/material/Backdrop'
import Fade from '@mui/material/Fade'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { GroupProps, profileType } from './GroupList'
import DammyImage from '@/assets/dummy-profile-image.svg'
import close_icon from '@/assets/close_icon.svg'
import { AddParticipantModal } from './AddParticipantModal'
import useRemoveSubAdminParticipant from '@/services/hooks/useRemoveSubAdminParticipant'

const SUB_ADMIN_ROLE = 'sub-admin'

const glassModalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100% - 24px)', sm: 400 },
  maxWidth: 400,
  outline: 'none',
  color: 'white',
  border: '1px solid rgb(75, 90, 135)',
  borderRadius: '20px',
  background: 'rgba(69, 68, 68, 0)',
  backdropFilter: 'blur(28px)',
  padding: { xs: '24px 16px', sm: '32px 24px' },
  textAlign: 'center' as const,
}

type Props = {
  goBack: () => void
  groupInfo: GroupProps | null
  onGroupInfoUpdate: (updated: GroupProps) => void
}

const GroupDetails: React.FC<Props> = ({
  groupInfo,
  goBack,
  onGroupInfoUpdate,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState<profileType | null>(null)
  const [removeError, setRemoveError] = useState('')

  const { mutate: removeParticipant, isPending: isRemoving } =
    useRemoveSubAdminParticipant()

  const handleParticipantsAdded = (newProfiles: profileType[]): void => {
    if (!groupInfo) return
    onGroupInfoUpdate({
      ...groupInfo,
      profileIds: [...groupInfo.profileIds, ...newProfiles],
    })
  }

  const handleConfirmRemove = (): void => {
    if (!groupInfo || !confirmRemove) return
    const userId = confirmRemove.userId?._id
    if (!userId) {
      setRemoveError('Cannot remove this participant: missing user ID.')
      setConfirmRemove(null)
      return
    }

    removeParticipant(
      { groupId: groupInfo._id, userId },
      {
        onSuccess: () => {
          onGroupInfoUpdate({
            ...groupInfo,
            profileIds: groupInfo.profileIds.filter(
              (p) => p.userId?._id !== userId,
            ),
          })
          setConfirmRemove(null)
        },
        onError: (err: unknown) => {
          const msg =
            (err as { message?: string })?.message ??
            'Failed to remove participant. Please try again.'
          setRemoveError(msg)
          setConfirmRemove(null)
        },
      },
    )
  }

  return (
    <div className="bg-[#FFFFFF1A] backdrop-blur-lg h-auto sm:!h-[550px] rounded-[25px] overflow-auto p-4 sm:p-6">
      <div>
        <div onClick={goBack} className="relative cursor-pointer">
          <img
            className="absolute left-0 top-0"
            src="./Left-icon.svg"
            alt="back icon"
          />
        </div>

        <div className="flex flex-col items-center justify-center">
          <p className="text-[22px] sm:text-[32px] font-bold">
            {groupInfo && groupInfo?.groupName}
          </p>
        </div>

        {/* Add Participant button */}
        <div className="flex justify-end mt-2">
          <button
            onClick={() => setAddModalOpen(true)}
            className="flex items-center gap-2 bg-[#FB5012] text-white px-4 py-2 rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <span className="text-lg leading-none">+</span>
            Add Participant
          </button>
        </div>
      </div>

      <div className="my-8 h-auto sm:h-[340px] overflow-y-auto">
        {groupInfo &&
          groupInfo?.profileIds?.length > 0 &&
          groupInfo?.profileIds.map((profile: profileType, index: number) => {
            const isSubAdmin = profile?.userId?.role === SUB_ADMIN_ROLE
            const isLast = groupInfo.profileIds.length === index + 1

            return (
              <div
                key={index}
                className={`flex items-center py-4 ${isLast ? '' : 'border-gradient border-gradient-chat only-bottom'}`}
              >
                <img
                  className="w-[56px] h-[56px] sm:w-[86px] sm:h-[86px] object-cover rounded-full border-4 border-[#ffffff] flex-shrink-0"
                  src={profile?.profilePic ? profile?.profilePic : DammyImage}
                  alt={profile?.firstName}
                />
                <div className="ml-4 flex flex-col items-start justify-center flex-1 min-w-0">
                  <p className="text-[16px] sm:text-[20px] capitalize text-[#BABABA]">
                    {profile?.userId?.role}
                  </p>
                  <p className="text-[18px] sm:text-[24px]">
                    {profile?.firstName} {profile?.lastName}
                  </p>
                </div>

                {isSubAdmin && (
                  <button
                    onClick={() => setConfirmRemove(profile)}
                    disabled={isRemoving}
                    className="ml-4 flex-shrink-0 text-[#959595] hover:text-[#FB5012] transition-colors disabled:opacity-40"
                    title="Remove from group"
                  >
                    {isRemoving &&
                    confirmRemove?.userId?._id === profile?.userId?._id ? (
                      <CircularProgress size={20} sx={{ color: '#FB5012' }} />
                    ) : (
                      <DeleteOutlineIcon sx={{ fontSize: 26 }} />
                    )}
                  </button>
                )}
              </div>
            )
          })}
      </div>

      {/* Add Participant Modal */}
      {groupInfo && (
        <AddParticipantModal
          open={addModalOpen}
          groupInfo={groupInfo}
          onClose={() => setAddModalOpen(false)}
          onSuccess={handleParticipantsAdded}
        />
      )}

      {/* Remove confirmation — glass style */}
      <Modal
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 400 } }}
      >
        <Fade in={!!confirmRemove}>
          <Box sx={glassModalStyle}>
            <div className="flex justify-end mb-2">
              <div
                className="w-fit h-fit p-[3px] cursor-pointer"
                onClick={() => setConfirmRemove(null)}
              >
                <img
                  src={close_icon}
                  alt="Close"
                  className="bg-[#ffffff1a] rounded-full"
                />
              </div>
            </div>
            <p className="text-[22px] font-bold mb-3">Remove Participant</p>
            <p className="text-[#959595] text-base mb-8">
              Remove{' '}
              <span className="text-white font-medium">
                {confirmRemove?.firstName} {confirmRemove?.lastName}
              </span>{' '}
              from this group?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setConfirmRemove(null)}
                disabled={isRemoving}
                className="border border-[#FB5012] text-[#FB5012] px-7 py-2.5 rounded-full text-lg capitalize disabled:opacity-40 hover:opacity-90 transition-opacity"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemove}
                disabled={isRemoving}
                className="flex items-center justify-center gap-2 bg-[#FB5012] text-white px-7 py-2.5 rounded-full text-lg capitalize disabled:opacity-40 hover:opacity-90 transition-opacity"
              >
                {isRemoving ? (
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                ) : null}
                {isRemoving ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </Box>
        </Fade>
      </Modal>

      {/* Remove error — glass style */}
      <Modal
        open={!!removeError}
        onClose={() => setRemoveError('')}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 400 } }}
      >
        <Fade in={!!removeError}>
          <Box sx={glassModalStyle}>
            <div className="flex justify-end mb-2">
              <div
                className="w-fit h-fit p-[3px] cursor-pointer"
                onClick={() => setRemoveError('')}
              >
                <img
                  src={close_icon}
                  alt="Close"
                  className="bg-[#ffffff1a] rounded-full"
                />
              </div>
            </div>
            <p className="text-[22px] font-bold mb-3">Error</p>
            <p className="text-[#959595] text-base mb-8">{removeError}</p>
            <button
              onClick={() => setRemoveError('')}
              className="bg-[#FB5012] text-white px-7 py-2.5 rounded-full text-lg capitalize hover:opacity-90 transition-opacity mx-auto block"
            >
              Close
            </button>
          </Box>
        </Fade>
      </Modal>
    </div>
  )
}

export default GroupDetails
